import { useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useMe } from "src/context/MeContext";

import type { useCustomSkillsQuery as useCustomSkillsQueryType } from "./__generated__/useCustomSkillsQuery.graphql";

const query = graphql`
  query useCustomSkillsQuery($orgId: GlobalID!) {
    node(id: $orgId) {
      ... on GraphQLOrganization {
        customSkills {
          id
          name
          description
          body
          sourceType
          githubRepoFullName
          githubDirectoryPath
          integration {
            id
            name
          }
        }
      }
    }
  }
`;

type NodeCustomSkills = NonNullable<
  NonNullable<useCustomSkillsQueryType["response"]["node"]>["customSkills"]
>;
export type CustomSkillSummaryType = NodeCustomSkills[number];

/**
 * Mirrors `common.skills.sanitize_skill_name` on the backend: the agent invokes
 * Skill calls using the sanitized form (lowercase, spaces/hyphens → `_`,
 * non-alphanumerics stripped), so the frontend must sanitize CustomSkill names
 * the same way before looking them up.
 */
export function sanitizeSkillName(name: string): string {
  return name
    .toLowerCase()
    .replace(/ /g, "_")
    .replace(/-/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

export function useCustomSkillsByName(): Map<string, CustomSkillSummaryType> {
  const me = useMe();
  const data = useLazyLoadQuery<useCustomSkillsQueryType>(
    query,
    { orgId: me.organization.id },
    { fetchPolicy: "store-or-network" }
  );
  const skills: ReadonlyArray<CustomSkillSummaryType> =
    data.node?.customSkills ?? [];
  return useMemo(() => {
    const map = new Map<string, CustomSkillSummaryType>();
    for (const skill of skills) map.set(sanitizeSkillName(skill.name), skill);
    return map;
  }, [skills]);
}
